<template>
  <div class="generator-review card p-4 mb-3" novalidate>
    <h4>{{ $t("signup.se.generator_review.company_address") }}</h4>
    <DynamicGeneratorField
      v-for="field in generatorFields"
      :key="field.title"
      :field="field"
      v-model="
        localGenerator.application_information.full_company_address[field.model]
      "
      translationPrefix="signup.se"
    />

    <div class="form-check mb-3">
      <input
        type="checkbox"
        class="form-check-input"
        id="sameAsCompanyAddress"
        v-model="sameAsCompanyAddress"
      />
      <label class="form-check-label" for="sameAsCompanyAddress">
        {{ $t("signup.se.generator_review.same_as_company_address") }}
      </label>
    </div>

    <template v-if="!sameAsCompanyAddress">
      <h4>{{ $t("signup.se.generator_review.invoice_address") }}</h4>
      <DynamicGeneratorField
        v-for="field in invoiceAddressFields"
        :key="`invoice-${field.title}`"
        :field="field"
        v-model="
          localGenerator.application_information.invoice_address[field.model]
        "
        translationPrefix="signup.se"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { useValidators } from "@/composables/useValidators";
import type { GeneratorFieldConfig } from "@/signup/DynamicGeneratorField.vue";
import DynamicGeneratorField from "@/signup/DynamicGeneratorField.vue";
import type { Account, Generator } from "@/types";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  generatorIndex: number;
  companyIndex: number;
  accountData: Account;
}>();

const emit = defineEmits<{
  handleUpdateGenerator: [generator: Generator];
}>();

const { t } = useI18n();
const validators = useValidators(t);

const generator = computed(() => {
  return props.accountData.companies[props.companyIndex].generators[
    props.generatorIndex
  ];
});

// Determine initial checkbox state based on whether invoice_address exists
const sameAsCompanyAddress = ref(
  !generator.value.application_information?.invoice_address
);

// Initialize with empty address objects if they don't exist
const localGenerator = reactive({
  ...generator.value,
  application_information: {
    ...generator.value.application_information,
    full_company_address: {
      postal_address: "",
      postal_code: "",
      city: "",
      ...generator.value.application_information?.full_company_address,
    },
    // Only initialize invoice_address if it exists in generator.value
    invoice_address:
      generator.value.application_information?.invoice_address || null,
  },
});

const generatorFields: GeneratorFieldConfig[] = [
  {
    title: "postal_address",
    model: "postal_address",
    type: "string",
    validation: [validators.required],
  },
  {
    title: "postal_code",
    model: "postal_code",
    type: "string",
    validation: [validators.required],
  },
  {
    title: "city",
    model: "city",
    type: "string",
    validation: [validators.required],
  },
];

const invoiceAddressFields = computed<GeneratorFieldConfig[]>(() => {
  const requiredIf = validators.requiredIf(!sameAsCompanyAddress.value);
  return [
    ...generatorFields,
    {
      title: "invoice_country",
      model: "country",
      type: "string",
      validation: [requiredIf],
    },
    {
      title: "invoice_co_address",
      model: "co_address",
      type: "string",
    },
    {
      title: "invoice_email",
      model: "email",
      type: "string",
      validation: [requiredIf],
    },
    {
      title: "specific_name",
      model: "specific_name",
      type: "string",
      validation: [requiredIf],
    },
    {
      title: "invoice_contact_telephone",
      model: "telephone",
      type: "string",
      validation: [requiredIf],
    },
    {
      title: "reference_number",
      model: "reference_number",
      type: "string",
    },
  ];
});

watch(
  [localGenerator, sameAsCompanyAddress],
  ([newValue, isSameAddress]) => {
    if (isSameAddress) {
      // Set invoice_address to null when addresses are the same
      localGenerator.application_information.invoice_address = null;
    } else if (!localGenerator.application_information.invoice_address) {
      // Initialize invoice_address if it's null and addresses are different
      localGenerator.application_information.invoice_address = {
        postal_address: "",
        postal_code: "",
        city: "",
      };
    }
    emit("handleUpdateGenerator", newValue);
  },
  { deep: true }
);
</script>
